<template>
  <Modal ref="modal" v-bind="{ redirectOnClose: true }">
    <div class="modal max-w-md mx-auto" slot-scope="{ closeModal }" @click.stop>
      <div class="modal__header">
        <h6 class="heading capitalize">New Package Item</h6>
      </div>
      <div class="modal__body">
        <ValidationObserver slim v-slot="{ handleSubmit, invalid }" ref="observer">
          <form class="space-y-16" @submit.prevent="handleSubmit(submit)">
            <div class="space-y-8">
              <ValidationProvider
                name="item"
                vid="productId"
                rules="required"
                v-slot="{ errors, failed }"
                slim
              >
                <BaseFormGroup>
                  <BaseFormLabel for="productId"> Product * </BaseFormLabel>
                  <VMultiselect
                    v-model="computedProduct"
                    label="name"
                    track-by="id"
                    placeholder="Select product/service"
                    open-direction="bottom"
                    :disabled="submitting || fetching"
                    :options="products"
                    :searchable="true"
                    :clear-on-select="true"
                    :close-on-select="true"
                    :options-limit="300"
                    :max-height="400"
                    :hide-selected="true"
                  >
                    <template slot="option" slot-scope="props">
                      <p class="mb-2">
                        {{ props.option.name }}
                      </p>
                      <span
                        v-if="props.option.type === 'service'"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"
                      >
                        Service
                      </span>
                      <span
                        v-if="props.option.type === 'basic'"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        Basic
                      </span>
                    </template>
                  </VMultiselect>
                  <BaseFormInlineMessage v-if="failed" danger>
                    {{ errors[0] }}
                  </BaseFormInlineMessage>
                </BaseFormGroup>
              </ValidationProvider>

              <ValidationProvider
                v-if="packageItemData.productId"
                name="name"
                vid="name"
                rules="required"
                v-slot="{ errors, failed }"
                slim
              >
                <BaseFormGroup class="w-full">
                  <BaseFormLabel for="name"> Name * </BaseFormLabel>
                  <BaseFormInput
                    id="name"
                    v-model="packageItemData.name"
                    :disabled="submitting || fetching"
                    :invalid="failed"
                    placeholder="enter name..."
                  />
                  <BaseFormInlineMessage v-if="failed" danger>
                    {{ errors[0] }}
                  </BaseFormInlineMessage>
                </BaseFormGroup>
              </ValidationProvider>
              <div class="flex-none">
                <label class="label custom-checkbox">
                  <input
                    type="checkbox"
                    @input="setOptional"
                    :disabled="submitting || fetching"
                    :checked="packageItemData.optional"
                  />
                  <span />
                  <span>optional</span>
                </label>
              </div>
              <ValidationProvider
                v-show="packageItemData.optional"
                key="packageItemPrice"
                name="price"
                vid="price"
                :rules="priceRules"
                v-slot="{ errors, failed }"
                slim
              >
                <BaseFormGroup class="w-32">
                  <BaseFormLabel for="price"> Price * </BaseFormLabel>
                  <BaseFormInput
                    id="price"
                    type="number"
                    step="0.01"
                    v-model="packageItemData.price"
                    :disabled="submitting || fetching"
                    :invalid="failed"
                    placeholder="enter price..."
                  />
                  <BaseFormInlineMessage v-if="failed" danger>
                    {{ errors[0] }}
                  </BaseFormInlineMessage>
                </BaseFormGroup>
              </ValidationProvider>
            </div>
            <div class="space-x-2 text-right">
              <BaseButton
                type="button"
                ghost
                :disabled="submitting || fetching"
                @click="closeModal"
              >
                Cancel
              </BaseButton>
              <BaseButton primary type="submit" :disabled="submitting || fetching || invalid">
                Submit
              </BaseButton>
            </div>
          </form>
        </ValidationObserver>
      </div>
    </div>
  </Modal>
</template>

<script>
import { mapActions } from 'vuex';
import Modal from '@/components/Ui/Modal.vue';
import { packageItemComputed } from '@/store/adminHelper';

export default {
  name: 'ModalPackageItemCreate',
  components: {
    Modal,
  },
  props: {
    productId: {
      type: String,
      required: true,
    },
    submitting: Boolean,
  },
  data: () => ({
    fetching: false,
    packageItemData: {
      name: '',
      productId: null,
      optional: false,
      price: 0,
    },
  }),
  computed: {
    ...packageItemComputed,
    computedProduct: {
      get() {
        if (!this.packageItemData.productId) {
          return null;
        }
        return this.$store.state.entities.products.byId[this.packageItemData.productId] || null;
      },
      set(product) {
        this.packageItemData.productId = product?.id || null;
      },
    },
    priceRules() {
      const { optional } = this.packageItemData;
      let minValue = 0;
      if (this.isUpgrade && this.packageItem) minValue = this.packageItem.price;
      return `${optional ? 'required|' : ''}decimal:2|min_value:${minValue}`;
    },
    upgradesPackageItemIdRules() {
      return this.isUpgrade ? 'required' : '';
    },
    upgradablePackageItems() {
      return this.packageItems.filter((packageItem) => packageItem.upgradesPackageItemId === null);
    },
    packageItem() {
      if (!this.packageItemData.upgradesPackageItemId) return null;
      return this.upgradablePackageItems.find(
        (o) => o.id === this.packageItemData.upgradesPackageItemId
      );
    },
  },
  watch: {
    'packageItemData.productId': {
      handler(productId) {
        if (!this.packageItemData.name && productId) {
          const product = this.products.find((o) => o.id === productId);
          this.packageItemData.name = product.name || '';
        }
      },
    },
    packageItem: {
      handler(packageItem) {
        this.packageItemData.price = packageItem ? this.packageItem.price : 0;
      },
      deep: true,
    },
  },
  created() {
    this.fetching = true;
    this.$store
      .dispatch('admin/products/fetchProductList', {
        params: {
          limit: 100,
          filter: {
            type: 'basic,service',
          },
        },
      })
      .then(() => {
        this.fetching = false;
        this.$nextTick(() => {
          if (this.$refs.product) {
            this.$refs.product.focus();
          }
        });
      })
      .catch(() => {
        this.fetching = false;
      });
  },
  methods: {
    ...mapActions('admin/products', ['fetchBasicProductList']),
    setOptional(ev) {
      if (this.isUpgrade) return;
      const { checked } = ev.target;
      this.packageItemData.optional = checked;
    },
    setUpgrade(ev) {
      const { checked } = ev.target;
      this.packageItemData.optional = checked;
      this.isUpgrade = checked;

      if (!checked) {
        this.packageItemData.upgradesPackageItemId = null;
      }
    },
    submit() {
      const next = this.$refs.modal.closeModal;
      this.$emit('submit', { postData: this.packageItemData, next });
    },
  },
};
</script>
